import api from '../../api/singleride';
import editApi from '../../api/editRide';
import pdfApi from '../../api/pdfGenerator';
import moment from 'moment';
import { router } from '../../main';
import { generateHtmlFromRideData } from '@/common/offlineGenerator';
import config from '@/common/config';
import { INTERNAL_HOME_PATH } from '@/common/appHome';
import { handleGuiVersionOutdated } from '@/common/guiVersionHandler';


/**
 * Seconds until the PDF rate limit lifts, read from a 429 response.
 *
 * The export asks for a file (responseType 'blob'), so even the JSON error body
 * arrives as a Blob and has to be read before it can be parsed. Returns null if
 * the number cannot be read; the message then falls back to general wording.
 */
async function retryAfterSeconds(response) {
  try {
    const body = response.data instanceof Blob ? JSON.parse(await response.data.text()) : response.data;
    const seconds = Number(body && body.retry_after);
    return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
  } catch (e) {
    return null;
  }
}

const state = {
  ride: {},
  rideId: null,
  rideHeader: {},
  lowedRideId: null,
  attractionForChangeingTheDate: null,
  newAttractionDateTime: null,
  rideUploaded: false,
  tripViewMode: 'list',
  showDay0AddActivityHint: false,
  showShareRideHint: false,
  onboarding: false,

};

const getters = {
  allRide: state => state.ride,
  rideId: state => state.rideId,
  rideHeader: state => state.rideHeader,
  lowedRideId: state => state.lowedRideId,
  attractionForChangeingTheDate: state => state.attractionForChangeingTheDate,
  newAttractionDateTime: state => state.newAttractionDateTime,
  rideUploaded: state => state.rideUploaded,
  rideType: state => {
    return typeof state?.ride?.ride?.ride_manager_id === 'number' ? 'private' : 'public';
  },
  rideTypeIsPrivate: state => {
    return typeof state?.ride?.ride?.ride_manager_id === 'number' ? true : false;
  },
  rideIsWhiteLabel: state => {
    return state?.ride?.ride?.is_white_label === true ? true : false;
  },
  isRideLocked: state => {
    return state?.rideHeader?.ride?.is_locked === true ? true : false;
  },
  tripViewMode: state => state.tripViewMode,
  showDay0AddActivityHint: state => state.showDay0AddActivityHint,
  showShareRideHint: state => state.showShareRideHint,
  isOnboarding: state => state.onboarding,
};

const actions = {
  cleanAllRideInformation({commit}){ 
    commit('setRide', {}); 
    commit('setRideHeader', {}); 
    commit('setLowedRideId', null); 
    commit('setAttractionForChangeingTheDate', null); 
    commit('setNewAttractionDateTime', null); 
    commit('setAttractionRideUploaded', false); 
  },

  updateAttractionRideUploadedWorkAroundInOrderToDisplayTheMainRideAfterFulyLoaded({commit}){
    commit('setAttractionRideUploaded', true);
  },
  async fetchRideWithLoadingPopup({ dispatch }) {
    dispatch( 'showModal', "loadingIcon" );
    await dispatch( 'fetchRide');
    dispatch( 'showModal', "" );
  },
  async fetchRideAndCalback({ rootState, state, commit, dispatch }) {

    const { apptoken } = rootState.appauth;
    const lowedRideId = state.lowedRideId;
    localStorage.setItem('lastRideId', lowedRideId);
    let response;
    try{
      response = await api.fetchRide(apptoken, lowedRideId);
      
    } 
    catch (error) {
      if (!navigator.onLine || error.message === 'Network Error') {
        window.location.href = '/offline_ride_view';
      } else {
        console.error('API error:', error);
      }
    }
 
    if( response.data.ServerMassageCode === 'gui_version_outdated' ){
      handleGuiVersionOutdated({ dispatch, rideInformationId: state.lowedRideId });
      return;
    }

    if( response.data.ServerMassageCode != "success" ){
      // we are popuping the error at the fetchRideHeaderInformation tha hapand befor
      return
    }

    try {
  
      const rideData = response.data;
      localStorage.setItem('lastRideData', JSON.stringify(rideData));
  
      // 1. Generate HTML
      const htmlContent = generateHtmlFromRideData(rideData);
  
      // 2. Create a response and cache it under a dynamic key
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const responseToCache = new Response(blob, { headers: { 'Content-Type': 'text/html' } });
  
      const cache = await caches.open(config.config.CACHE_NAME);
      // const url = `/offline_ride_view/${lowedRideId}`;
      // await cache.put(url, responseToCache);
      const lastUrl = `/offline_ride_view`;
      await cache.put(lastUrl, responseToCache);

      // console.log('[App] Offline ride HTML cached at:', lastUrl);
    } 
    catch (error)
    {
      // console.error('[App] Failed to fetch and cache ride:', error);
    }

    // if ('caches' in window) {
    //   const cache = await caches.open('tripplanner-data');
    //   const clonedResponse = new Response(JSON.stringify(response.data), {
    //     headers: { 'Content-Type': 'application/json' }
    //   });
    //   await cache.put(cacheKey, clonedResponse);
    //   console.log("💾 Cached ride data under:", cacheKey);
    // }

    commit('setRide', response.data);
    
    // Set language from rideHeader if available (language is located at rideHeader.ride.language)
    const rideLanguage = state.rideHeader?.ride?.language;
    if (rideLanguage) {
      dispatch('language/setLanguageFromRide', rideLanguage, { root: true });
    }
    
    dispatch('buildAccommodationIcons');
    dispatch('updateTheRideUploadedWorkAroundInOrderToDisplayTheMainRideAfterFulyLoaded');
    
    return response;

  },
  async updateTheRideUploadedWorkAroundInOrderToDisplayTheMainRideAfterFulyLoaded({commit}){
    return new Promise(resolve => {
      setTimeout(() => {
        commit('setAttractionRideUploaded', true);
        resolve(); // Resolve the promise after the delay
      }, 1000); // 1000 milliseconds = 1 second
    });



    // commit('setAttractionRideUploaded', true);
  },
  async fetchRideHeaderInformation({ rootState, state, commit, dispatch }) {
    const { apptoken } = rootState.appauth;
    const lowedRideId = state.lowedRideId;
    const response = await api.fetchRideHeader(apptoken, lowedRideId);
    if (response.data.ServerMassageCode != "success") {
      if (response.data.ServerMassageCode == "user_does_not_have_permission_to_this_private_ride") {
        const rideLanguage = response.data?.ride?.language;
        const isHebrew =
          rideLanguage &&
          (rideLanguage.toLowerCase() === "he" || rideLanguage.toLowerCase() === "hebrew");

        if (isHebrew) {
          router.push('/private_page_landing/' + lowedRideId);
        } else {
          router.push('/private_page_landing/' + lowedRideId + '?lang=en');
        }
        return;
      }
      dispatch( 'showModalWithParameters',
      { errorNumber : response.data.ServerMassageCode,
        type:'textConverter' 
      } );
      return
    }
    commit('setRideHeader', response.data);
    
    // Set language from rideHeader (language is located at rideHeader.ride.language)
    const rideLanguage = response.data?.ride?.language;
    if (rideLanguage) {
      dispatch('language/setLanguageFromRide', rideLanguage, { root: true });
    }
    
    return response
  },
  async fetchRide({ dispatch }) {
    var response = await dispatch('fetchRideAndCalback');
    if( !response){
      return
    }
    for( var i=0; i < response.data.attractionsByDay.length; i++ ){
      var attractionpoints = await dispatch( 'updatemarkerByColers', response.data.attractionsByDay[i].daily_attractions );
      response.data.attractionsByDay[i].daily_attractions = attractionpoints
    }

    var highLevelRideLocations = await dispatch( 'updatemarkerByColersAndLetter', response.data.highLevelRideLocations );
    // if it is empty we van not do the next step
    dispatch( 'addMap', { elementId:"map_canvas",
                          points: highLevelRideLocations });
  },
  updateMainRideScroleLocation( /* eslint-disable-line no-unused-vars */ _ ){
    localStorage.setItem('workAroundToSetTheScroleLocationAtTheMAinPage', window.top.scrollY);
  },
  userGuideForEditing( {getters} ){
    // window.localStorage.removeItem('dont_display_user_guide_for_editing_massage');
    // TODO -------- thsi one is not removed after getin to y=2000
    const rideType = getters.rideType;
    var guidePresented = window.localStorage.getItem('dont_display_user_guide_for_editing_massage')
    if( guidePresented || rideType != 'public'){
      window.removeEventListener('scroll', this.userGuideForEditing);
      return
    }
    // if( window.top.scrollY > 2000 && !guidePresented ){
    //   dispatch( 'showModalWithParameters',
    //             { errorNumber : "user_guide_for_editing",
    //               type:'textConverter' 
    //             } );
    //   window.localStorage.setItem('dont_display_user_guide_for_editing_massage', true);
    // }
  },
  async uploadRide({ rootState }, ride) {// this one is not in use!!!!!!!!!
    // Get the access token
    const { apptoken } = rootState.appauth;

    // Call our API module to do the upload
    await api.uploadRide(ride, apptoken);

    // Redirect our user to ImageList component
    router.push(INTERNAL_HOME_PATH);
  },
  ReturnToSingleRide({ rootState }) {
    const rideId = rootState.ride.rideId;
    router.push('/single_ride_with_map/' + rideId );
  },
  async removeDayFromRide({ rootState, dispatch }, { dayIndex, force = false }) {
    const { apptoken } = rootState.appauth;
    const rideId = rootState.ride.rideId;
    const response = await editApi.removeDayFromRide( apptoken, rideId, dayIndex, force );
    
    // Return false if there's an error
    if( response.data.ServerMassageCode != "secsses" ){
      // If we get ordered accommodation error and force is false, show confirmation modal
      if( response.data.ServerMassageCode == "rideHaveOrderedAccommodation" && !force ){
        dispatch( 'showModalWithParameters',
                   { inerModalParameters: { dayIndex: dayIndex, removeType: 'dayOnly' },
                     type:'confirmForceRemoveDay' 
                   } );
        return false;
      }
      
      // For other errors, show the error message
      dispatch( 'showModalWithParameters',
                { errorNumber : response.data.ServerMassageCode,
                  type:'textConverter' 
                } );
      return false;
    }

    await dispatch('fetchRide');
    dispatch('fetchRideHeaderInformation');
    
    // Return true on success
    return true;
  },
  async addNewDayToRide({ rootState, dispatch }, { dayIndex, force = false }) {
    const rideId = rootState.ride.rideId;
    const { apptoken } = rootState.appauth;
    const response = await editApi.addNewDayToRide( apptoken, rideId, dayIndex, force);
    
    // Return false if there's an error
    if( response.data.ServerMassageCode != "secsses" ){
      // If we get ordered accommodation error and force is false, show confirmation modal
      if( response.data.ServerMassageCode == "rideHaveOrderedAccommodation" && !force ){
        dispatch( 'showModalWithParameters',
                   { inerModalParameters: { dayIndex: dayIndex },
                     type:'confirmForceAddDay' 
                   } );
        return false;
      }
      
      // For other errors, show the error message
      dispatch( 'showModalWithParameters',
                { errorNumber : response.data.ServerMassageCode,
                  type:'textConverter' 
                } );
      return false;
    }

    await dispatch('fetchRide');
    dispatch('fetchRideHeaderInformation');
    
    // Return true on success
    return true;
  },

  async updateRideDuration({ rootState, dispatch }, newRideDates ) {
    const rideId = rootState.ride.rideId;
    const { apptoken } = rootState.appauth;
    const response = await editApi.updateRideDuration( apptoken, rideId, newRideDates);
    if( response.data.ServerMassageCode != "secsses" ){
      dispatch( 'showModalWithParameters',
                { errorNumber : response.data.ServerMassageCode,
                  type:'textConverter' 
                } );
    }
    await dispatch('fetchRide');
  },
  async getTheClosestAttractionStartFromGoingDown( { rootState },dayIndex ){
    const ride = rootState.ride;
    while( (ride.ride.attractionsByDay[dayIndex].daily_attractions.length == 0) && ( dayIndex > 0 )){
      dayIndex = dayIndex - 1;
    }
    // serche after the date if not exist
    if( ride.ride.attractionsByDay[dayIndex].daily_attractions.length == 0 ){
      while(  (ride.ride.attractionsByDay[dayIndex].daily_attractions.length == 0) &&
              ( ( ride.ride.attractionsByDay.length - 1 ) > dayIndex ) ) {
        dayIndex = dayIndex + 1;
      }
    }
    const dylyactivity = ride.ride.attractionsByDay[dayIndex]
    var i;
    var lastLng = 0;
    var lastLat = 0;
    for( i = 0; i < dylyactivity.daily_attractions.length; i++ ){
      if( dylyactivity.daily_attractions[i].latitude ){
        lastLat = dylyactivity.daily_attractions[i].latitude;
        lastLng = dylyactivity.daily_attractions[i].longtitude;
      }
    }
    return{ 'lat': lastLat, 'lng':lastLng }

  },
  async addhttp( _, url ) {
    // We have an RIGHT-TO-LEFT MARK at the end of the string ( at some links )
    // and we should remove them!
    var statickUrl = await url;
    if ( !/^(?:f|ht)tps?:/i.test( statickUrl ) ){
      statickUrl =  'http://'.concat( statickUrl )
    }
    // return statickUrl.replace(/[^\x00-\x7F]/g, "");
    return statickUrl.replace(/[^\x20-\x7F]/g, "");
  },

  async serchAttractionToRide({ rootState, commit, dispatch }, dayIndex ) {
    const ride = rootState.ride
    const originalDayIndex = dayIndex;
    const point =  await dispatch('getTheClosestAttractionStartFromGoingDown', dayIndex );
    
    var lat = point.lat;
    var lng = point.lng;
    if( ride.ride.attractionsByDay[originalDayIndex].daily_attractions.length == 0 ){
      if( ride.ride.attractionsByDay[originalDayIndex].accommodation.latitude ){//                 wehave an booking information for this morning)){
        lat = ride.ride.attractionsByDay[originalDayIndex].accommodation.latitude
        lng = ride.ride.attractionsByDay[originalDayIndex].accommodation.longtitude
      }
      else if( ride.ride.attractionsByDay[originalDayIndex].base_trip_accommodation.latitude ){//    wehave an booking information for this morning)){
        lat = ride.ride.attractionsByDay[originalDayIndex].base_trip_accommodation.latitude
        lng = ride.ride.attractionsByDay[originalDayIndex].base_trip_accommodation.longtitude
      }
    }

    const rawDate = ride.ride.attractionsByDay[originalDayIndex].dateTime;
    const dateStr = new Date(rawDate).toISOString().split("T")[0]; // Get "2025-08-02"
    const [year, month, day] = dateStr.split("-").map(Number);
    const palannedDate = new Date(year, month - 1, day, 23, 59); // Local time without shifting
    commit('setSerchPlannedDate', palannedDate.toLocaleString("en-US"));
    commit('setSerchLat', lat);
    commit('setSerchLng', lng);
    commit('setSerchDayIndex', originalDayIndex);
    router.push('/serch_attractions_with_map');
  },
  async removeAttractionFromRide( { rootState, dispatch }, attractionId){
    const rideId = rootState.ride.rideId;
    const { apptoken } = rootState.appauth;
    dispatch( 'showModal', "loadingIcon" );
    await editApi.removeAttractionFromRide( apptoken, rideId, attractionId );
    dispatch( 'showModal', "" );
    await dispatch( "fetchRide" );
  },
  async updateAttractionTimeAtRide( { dispatch, rootState, state }){
    const rideId = rootState.ride.rideId;
    const attractionId = state.attractionForChangeingTheDate.id
    const attractionNewTime = state.newAttractionDateTime
    const { apptoken } = rootState.appauth;
      await editApi.updateAttractionRideTime(  apptoken,
                                               rideId,
                                               attractionId,
                                               attractionNewTime );
    router.push('/single_ride_with_map/' + rideId );
    dispatch('fetchRideAndCalback');
  },

  async reorderAttractionsInDay({ dispatch, rootState }, { date, orderedAttractions, movedAttractionId }) {
    const rideId = rootState.ride.rideId;
    const { apptoken } = rootState.appauth;

    const getHM = (attraction) => {
      const dt = attraction?.dateTime ? moment.utc(attraction.dateTime) : null;
      return (dt && dt.isValid()) ? { h: dt.hour(), m: dt.minute() } : { h: 23, m: 59 };
    };

    // Determine target H:M for the moved attraction based on its new neighbor
    const movedIndex = orderedAttractions.findIndex(a => a.id === movedAttractionId);

    // Single attraction in the day — just move it with its original time
    if (orderedAttractions.length === 1) {
      const dt = orderedAttractions[0].dateTime ? moment.utc(orderedAttractions[0].dateTime) : null;
      const timeStr = (dt && dt.isValid()) ? dt.format('hh:mm:ss A') : '11:59:00 PM';
      await editApi.updateAttractionRideTime(apptoken, rideId, orderedAttractions[0].id, `${date}, ${timeStr}`);
      dispatch('fetchRideAndCalback');
      return;
    }

    let targetHM;
    if (movedIndex > 0) {
      targetHM = getHM(orderedAttractions[movedIndex - 1]); // adopt prev's H:M
    } else {
      targetHM = getHM(orderedAttractions[1]); // first position: adopt next's H:M
    }

    // Build each attraction's effective H:M (moved one gets targetHM, others keep their own)
    const withHM = orderedAttractions.map((a, i) => {
      const hm = (a.id === movedAttractionId) ? targetHM : getHM(a);
      return { attraction: a, h: hm.h, m: hm.m, orderIndex: i };
    });

    // Group by H:M, preserving order within each group
    const groups = {};
    withHM.forEach(item => {
      const key = `${item.h}:${String(item.m).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    // Assign seconds within each group and collect changed attractions
    const updates = [];
    Object.values(groups).forEach(group => {
      group.forEach((item, slotIndex) => {
        const currentDt = item.attraction.dateTime ? moment.utc(item.attraction.dateTime) : null;
        const alreadyCorrect = currentDt && currentDt.isValid() &&
          currentDt.hour() === item.h &&
          currentDt.minute() === item.m &&
          currentDt.second() === slotIndex;
        if (alreadyCorrect) return;

        const timeStr = moment().hour(item.h).minute(item.m).second(slotIndex).format('hh:mm:ss A');
        updates.push(editApi.updateAttractionRideTime(apptoken, rideId, item.attraction.id, `${date}, ${timeStr}`));
      });
    });

    if (updates.length > 0) {
      await Promise.all(updates);
      dispatch('fetchRideAndCalback');
    }
  },

  async updateRideCategory( { rootState }, rideCategoryInformation ){
    const rideId = rideCategoryInformation.rideId;
    const category = rideCategoryInformation.newCategory;
    const { apptoken } = rootState.appauth;
    await editApi.updateRideCategory( apptoken,
                                      rideId,
                                      category );
  },
  async removeDayAndAttractionsFromRide({ rootState, dispatch }, { dayIndex, force = false }) {
    const { apptoken } = rootState.appauth;
    const rideId = rootState.ride.rideId;
    const response = await editApi.removeDayAndAttractionsFromRide( apptoken, rideId, dayIndex, force );
    
    // Return false if there's an error
    const errorCode = response.data?.ServerMassageCode;
    if( errorCode && errorCode !== "secsses" ){
      // If we get ordered accommodation error and force is false, show confirmation modal
      if( errorCode == "rideHaveOrderedAccommodation" && !force ){
        dispatch( 'showModalWithParameters',
                   { inerModalParameters: { dayIndex: dayIndex, removeType: 'dayAndAttractions' },
                     type:'confirmForceRemoveDay' 
                   } );
        return false;
      }
      
      // For other errors, show the error message
      dispatch( 'showModalWithParameters',
                { errorNumber : errorCode,
                  type:'textConverter' 
                } );
      return false;
    }

    await dispatch('fetchRide');
    dispatch('fetchRideHeaderInformation');
    
    // Return true on success
    return true;
  },

  async moveDayAttractions({ state, rootState, dispatch }, { dayIndex } ) {
    const { apptoken } = rootState.appauth;
    const rideId = rootState.ride.rideId;
    // Prefer the provided value; fall back to the one stored in state
    const newTime = state.newAttractionDateTime;
    const response = await editApi.moveDayAttractions( apptoken, rideId, dayIndex, newTime );
    if( response.data && response.data.ServerMassageCode && response.data.ServerMassageCode !== "secsses" ){
      dispatch( 'showModalWithParameters',
                { errorNumber : response.data.ServerMassageCode,
                  type:'textConverter' } );
    }
    await dispatch('fetchRide');
    dispatch('fetchRideHeaderInformation');
  },
  updateTripViewMode({ commit }, mode) {
    commit('setTripViewMode', mode);
  },
  async resetRideToOriginal({ rootState, dispatch }, rideId) {
    const { apptoken } = rootState.appauth;
    if (apptoken) {
      try {
        const response = await editApi.resetRideToCopiedRideApi(apptoken, rideId);
        
        if( response.data.ServerMassageCode != "success" ){
          dispatch('showModalWithParameters', {
            errorNumber: 'ride_reset_error',
            type: 'textConverter'
          });
          return;
        }
        // Refresh the ride data after reset to get the latest updates
        await dispatch('fetchRide');
        dispatch('fetchRideHeaderInformation');
        
        dispatch('showModalWithParameters', {
          errorNumber: 'ride_reset_success',
          type: 'textConverter'
        });
      } catch (error) {
        console.error('Failed to reset ride:', error);
        dispatch('showModalWithParameters', {
          errorNumber: 'ride_reset_error',
          type: 'textConverter'
        });
      }
    }
  },
  async fetchBrochurePhotoOptions({ rootState }, rideId) {
    const { apptoken } = rootState.appauth;
    const response = await pdfApi.getBrochurePhotoOptions(apptoken, rideId);
    return response.data;
  },
  async exportTripToPDF({ rootState, dispatch }, { rideId, tripTitle, style = 'itinerary', photos = null }) {
    const { apptoken } = rootState.appauth;

    try {
      const response = await pdfApi.generateTripPDF(apptoken, rideId, style, photos);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from response header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${tripTitle}_${style === 'brochure' ? 'brochure' : 'itinerary'}.pdf`;
      if (contentDisposition) {
        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      dispatch('showModalWithParameters', {
        errorNumber: 'pdf_generated_successfully',
        type: 'textConverter'
      });
      
      return { success: true };
      
    } catch (error) {
      console.error('Error generating PDF:', error);

      if (error.response && error.response.status === 429) {
        dispatch('showModalWithParameters', {
          errorNumber: 'pdf_rate_limited',
          type: 'textConverter',
          textParams: { seconds: await retryAfterSeconds(error.response) },
        });
      } else {
        dispatch('showModalWithParameters', {
          errorNumber: 'pdf_generation_failed',
          type: 'textConverter'
        });
      }

      return { success: false, error };
    }
  },
};

const mutations = {
  setRide: (state, ride) => {
    state.ride = ride;
    if( ride.ride ){
      state.rideId = ride.ride.id;
    }
    else {
      state.rideId = null
    }
  },
  setRideHeader: (state, rideHeader) => {
    state.rideHeader = rideHeader;
  },
  setLowedRideId: (state, lowedRideId) => {
    state.lowedRideId = lowedRideId;
  },
  setAttractionForChangeingTheDate: (state, attractionForChangeingTheDate) => {
    // it can be an attraction or day at the trip!
    if( attractionForChangeingTheDate ){
      state.attractionForChangeingTheDate = attractionForChangeingTheDate;
      const originalDateTime = new Date(state.attractionForChangeingTheDate.dateTime.replace('GMT','') );
      state.newAttractionDateTime = moment(originalDateTime).format('M/D/YYYY, hh:mm:ss A');
    }
    else{
      state.attractionForChangeingTheDate = attractionForChangeingTheDate
      state.newAttractionDateTime = null
    }
  },
    setNewAttractionDateTime: (state, newAttractionDateTime) => {
    state.newAttractionDateTime = newAttractionDateTime;
  },
  setNewAttractionDate: (state, newAttractionDate) => {
    state.newAttractionDateTime = newAttractionDate + ", " + state.newAttractionDateTime.split(",")[1];
  },
  setNewAttractionTime: (state, newAttractionTime) => {
    state.newAttractionDateTime = state.newAttractionDateTime.split(",")[0] + ", " + newAttractionTime;
  },
  setAttractionRideUploaded: ( state, rideUploaded ) => {
    state.rideUploaded = rideUploaded;
  },
  setTripViewMode(state, mode) {
    state.tripViewMode = mode;
  },
  setShowDay0AddActivityHint(state, value) {
    state.showDay0AddActivityHint = !!value;
  },
  setShowShareRideHint(state, value) {
    state.showShareRideHint = !!value;
  },
  setOnboarding(state, value) {
    state.onboarding = !!value;
  },
};

export default {
  state,
  getters,
  actions,
  mutations
};
